import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Logo } from "@/components/Logo";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const toggleLang = () => setLang(lang === "es" ? "en" : "es");

  return (
    <header
      className="sticky top-0 z-40 w-full backdrop-blur-xl bg-white/80 border-b border-slate-100"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-home-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <a
            href="#home"
            className="text-sm font-medium text-slate-600 hover:text-green-700 transition-colors"
            data-testid="nav-link-home"
          >
            {t.nav.home}
          </a>

          {/* Quiropráctica dropdown with Terapia Física sub-item */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <a
              href="#services-quiro"
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-green-700 transition-colors"
              data-testid="nav-link-quiro"
              aria-expanded={servicesOpen}
              aria-haspopup="true"
            >
              {t.nav.servicesQuiro}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </a>

            {servicesOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                data-testid="services-dropdown"
              >
                <div className="min-w-[200px] rounded-2xl border border-slate-100 bg-white shadow-xl shadow-slate-900/5 p-2">
                  <a
                    href="#services-fisica"
                    onClick={() => setServicesOpen(false)}
                    className="block rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                    data-testid="nav-services-fisica"
                  >
                    {t.nav.servicesFisica}
                  </a>
                </div>
              </div>
            )}
          </div>

          <a
            href="#alternative"
            className="text-sm font-medium text-slate-600 hover:text-green-700 transition-colors"
            data-testid="nav-link-alternative"
          >
            {t.nav.alternative}
          </a>
          <a
            href="#estetica"
            className="text-sm font-medium text-slate-600 hover:text-green-700 transition-colors"
            data-testid="nav-link-estetica"
          >
            {t.nav.estetica}
          </a>
          <a
            href="#booking-fisio"
            className="text-sm font-medium text-slate-600 hover:text-green-700 transition-colors"
            data-testid="nav-link-booking"
          >
            {t.nav.booking}
          </a>
          <Link
            to="/#blog"
            className="text-sm font-medium text-slate-600 hover:text-green-700 transition-colors"
            data-testid="nav-link-blog"
          >
            {t.nav.blog}
          </Link>
          <Link
            to="/quiz"
            className="text-sm font-semibold text-green-700 hover:text-green-900 transition-colors inline-flex items-center gap-1"
            data-testid="nav-link-quiz"
          >
            Quiz <span className="text-[9px] uppercase tracking-widest bg-green-100 text-green-800 px-1.5 py-0.5 rounded">Nuevo</span>
          </Link>
          <a
            href="#about"
            className="text-sm font-medium text-slate-600 hover:text-green-700 transition-colors"
            data-testid="nav-link-about"
          >
            {t.nav.about}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleLang}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-green-300 hover:text-green-700 transition-colors"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === "es" ? "ES" : "EN"}
          </button>
          <a
            href="#booking-fisio"
            className="hidden sm:inline-flex items-center rounded-full bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700 transition-colors shadow-sm hover:shadow"
            data-testid="nav-cta-book"
          >
            {t.nav.cta}
          </a>
          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-slate-700"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-4">
            <a href="#home" onClick={() => setOpen(false)} className="text-base font-medium text-slate-700" data-testid="mobile-nav-link-home">{t.nav.home}</a>
            <div className="border-l-2 border-green-100 pl-3">
              <a href="#services-quiro" onClick={() => setOpen(false)} className="block py-1 text-base font-medium text-slate-700" data-testid="mobile-nav-link-quiro">{t.nav.servicesQuiro}</a>
              <a href="#services-fisica" onClick={() => setOpen(false)} className="block py-1 text-sm font-medium text-slate-600" data-testid="mobile-nav-link-fisica">{t.nav.servicesFisica}</a>
            </div>
            <a href="#alternative" onClick={() => setOpen(false)} className="text-base font-medium text-slate-700" data-testid="mobile-nav-link-alternative">{t.nav.alternative}</a>
            <a href="#estetica" onClick={() => setOpen(false)} className="text-base font-medium text-slate-700" data-testid="mobile-nav-link-estetica">{t.nav.estetica}</a>
            <a href="#booking-fisio" onClick={() => setOpen(false)} className="text-base font-medium text-slate-700" data-testid="mobile-nav-link-booking">{t.nav.booking}</a>
            <Link to="/#blog" onClick={() => setOpen(false)} className="text-base font-medium text-slate-700" data-testid="mobile-nav-link-blog">{t.nav.blog}</Link>
            <Link to="/quiz" onClick={() => setOpen(false)} className="text-base font-semibold text-green-700" data-testid="mobile-nav-link-quiz">Quiz</Link>
            <a href="#about" onClick={() => setOpen(false)} className="text-base font-medium text-slate-700" data-testid="mobile-nav-link-about">{t.nav.about}</a>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={toggleLang}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700"
                data-testid="mobile-lang-toggle"
              >
                <Globe className="h-3.5 w-3.5" />
                {lang === "es" ? "ES" : "EN"}
              </button>
              <a
                href="#booking-fisio"
                onClick={() => setOpen(false)}
                className="inline-flex flex-1 justify-center items-center rounded-full bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
                data-testid="mobile-nav-cta-book"
              >
                {t.nav.cta}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
